import { Country } from '../../../types/country';
import { PaymentDetails } from './payment-details';
import { PaymentSummary } from './payment-summary';
import { IdentityVerificationCaseV1UpdateInput, PaymentInstrument } from '../../../generated';
import { PaymentInstrumentDisplay } from './payment-instrument-display';
import { Form, Button, theme, Grid, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { getItem } from '../local-storage';
import {UpdateCardModalContainer} from '../../layouts/applicant/components/update-card-modal.container';
import { useBeforeUnload } from '../../layouts/applicant/shared/components/custom-hooks';
 
interface PaymentInformationProps {
  caseId: string;
  countries: Country[];
  serviceFee: number;
  paymentInstrument?: PaymentInstrument;
  cyberSourcePublicKey: string;
  onSubmit: (data: IdentityVerificationCaseV1UpdateInput) => Promise<void>;
  onWithdrawRequest: () => void;
}
 
export const PaymentInformation: React.FC<PaymentInformationProps> = (props) => {
  const [isComponentLoaded, setIsComponentLoaded] = useState(true);
  const {
    token: { colorBgContainer, borderRadiusLG }
  } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const applicantUserId = getItem('applicantUserId');
  const [isUpdatingApplicant, setIsUpdatingApplicant] = useState(false);
  const [generatePaymentToken, setGeneratePaymentToken] = useState<() => Promise<void>>();
  const [isOpen, setIsOpen] = useState(false);

  const onSubmit = async () => {
    setIsComponentLoaded(false);
    const paymentToken = form.getFieldValue('paymentInstrumentToken');
    if (!paymentToken && generatePaymentToken) {
      await generatePaymentToken();
    }
    form
      .validateFields()
      .then((values) => {
        const casePaymentDetails: IdentityVerificationCaseV1UpdateInput = {
          id: props.caseId,
          payment: {
            paymentAmount: props.serviceFee,
            paymentInstrumentId: props?.paymentInstrument?.paymentInstrumentId
          }
        };
        if (!props?.paymentInstrument?.paymentInstrumentId && casePaymentDetails.payment) {
          casePaymentDetails.payment.paymentInstrument = {
            billingAddressLine1: values.address,
            billingAddressLine2: values.billingAddressLine2,
            billingCity: values.city,
            billingState: values.state,
            billingPostalCode: values.billingPostalCode,
            billingCountry: values.country,
            billingEmail: values.billingEmail,
            billingPhone: values.billingPhone,
            billingFirstName: values.billingFirstName,
            billingLastName: values.billingLastName,
            paymentToken: values.paymentInstrumentToken
          };
        }
        props.onSubmit(casePaymentDetails);
        console.log('Values', JSON.stringify(casePaymentDetails, null, 2));
      })
      .catch((error) => {
        if (error.errorFields && error.errorFields.length > 0) {
          const firstErrorField = error.errorFields[0].name;
          form.scrollToField(firstErrorField, {
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
        setIsUpdatingApplicant(false);
        console.log('Form Error', error);
      });
  };
 
  const actionButton = (
    <Button type="primary" onClick={() => setIsOpen(true)}>
      Update card Info
    </Button>
  );
 
  const navigate = useNavigate();
   //block-if-not-saved
   useBeforeUnload(isComponentLoaded, "Changes that you made may not be saved.");

  return (
    <>
      <div
        style={{
          background: colorBgContainer,
          width: screens.xs ? '100%' : '70%',
          minWidth: 280,
          padding: screens.xs ? '24px' : '48px',
          borderRadius: borderRadiusLG,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'baseline'
        }}
      >
        <div className={!props.paymentInstrument ? 'text-3xl font-medium mb-2' : ''}>Payment Information</div>
        <PaymentSummary serviceFee={props.serviceFee} cardOnFile={!!props.paymentInstrument} />
        {props.paymentInstrument ? (
          <>
            <PaymentInstrumentDisplay paymentInstrument={props.paymentInstrument} actionButton={actionButton} />
            <UpdateCardModalContainer
              applicantUserId={applicantUserId}
              cyberSourcePublicKey={props.cyberSourcePublicKey}
              countries={props.countries}
              paymentInstrumentId={props.paymentInstrument.paymentInstrumentId}
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
            />
          </>
        ) : (
          <PaymentDetails countries={props.countries} cyberSourcePublicKey={props.cyberSourcePublicKey} form={form} setGeneratePaymentToken={setGeneratePaymentToken}/>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '24px 0px',
          gap: screens.xs ? '10px' : '0',
          width: screens.xs ? '100%' : '70%'
        }}
      >
        <div>
          <Popconfirm title= {isComponentLoaded? 'Are you sure? Changes you made will not be saved.' : null}
            onConfirm={() => {setIsComponentLoaded(false); navigate(`/applicant/onboarding/id-verification-preview`)}} disabled={!isComponentLoaded}>
            <Button onClick={()=>isComponentLoaded ? null : navigate(`/applicant/onboarding/id-verification-preview`)} style={{ height: screens.xs ? '50px' : 'auto' }}>
              {'<< Back'}
            </Button>
          </Popconfirm>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: screens.xs ? 'column' : 'row',
            gap: '15px'
          }}
        >
          <Popconfirm title="Are you sure? Your entries on this page will be deleted." onConfirm={() => props.onWithdrawRequest()}>
            <Button danger style={{ height: screens.xs ? '50px' : 'auto' }} loading={isUpdatingApplicant}>
              Withdraw Request
            </Button>
          </Popconfirm>
          <Button type="primary" onClick={() => onSubmit()} style={{ height: screens.xs ? '50px' : 'auto' }} disabled={isUpdatingApplicant}>
            {'Complete Request'}
          </Button>
        </div>
      </div>
    </>
  );
};